<template>
  <div class="operation-flags-card" :class="{ 'operation-flags-card--inline': inline }">
    <div v-if="!inline" class="text-muted text-uppercase small fw-semibold mb-1">
      {{ $t("financialSummary.stages.flagsTitle") }}
    </div>

    <!-- Banderas asignadas: mismo badge que la columna de banderas del listado.
         Un clic en el badge abre el popup del color de esa bandera. -->
    <div v-if="selected.length" class="operation-flags-card__tags" :class="{ 'mb-1': !inline }">
      <FlagBadge
        v-for="name in selected"
        :key="name"
        :name="name"
        :catalog="catalog"
        :title="colorPickable ? $t('financialSummary.stages.flagColorHintOnBadge') : name"
        class="operation-flags-card__tag"
        :class="{ 'operation-flags-card__tag--pickable': colorPickable }"
        @click.native="pickColor(name)"
      >
        <button
          v-if="!disabled"
          type="button"
          class="operation-flags-card__remove"
          :title="$t('Delete')"
          :aria-label="$t('Delete')"
          @click.stop.prevent="remove(name)"
        >&times;</button>
      </FlagBadge>
    </div>

    <!-- En línea el select no está a la vista: un + lo despliega, así el encabezado
         del embarque no crece de alto. -->
    <button
      v-if="inline && !pickerOpen"
      type="button"
      class="operation-flags-card__add"
      :disabled="disabled"
      :title="$t('financialSummary.stages.flagsPlaceholder')"
      :aria-label="$t('financialSummary.stages.flagsPlaceholder')"
      @click.stop.prevent="openPicker"
    >
      <i class="fas fa-plus" aria-hidden="true"></i>
    </button>

    <!-- Cada opción elegida se suma como tag arriba, y la última opción crea una
         bandera nueva. Las banderas van con la jerarquía y el color de la lista. -->
    <multiselect
      v-if="!inline || pickerOpen"
      ref="picker"
      class="operation-flags-card__select"
      :class="{ 'operation-flags-card__select--inline': inline }"
      :value="null"
      :options="pickerOptions"
      :disabled="disabled"
      :searchable="true"
      :allow-empty="true"
      :show-labels="false"
      :close-on-select="true"
      label="name"
      track-by="key"
      :placeholder="available.length ? $t('financialSummary.stages.flagsPlaceholder') : $t('financialSummary.stages.flagsNoOptions')"
      @select="onSelect"
      @close="closePicker"
    >
      <template #option="{ option }">
        <!-- Crear una bandera sin salir de acá: abre el popup de nombre y color. -->
        <span v-if="option.create">{{ option.name }}</span>
        <FlagOptionLabel v-else :label="option.name" :color="option.color" :depth="option.depth" />
      </template>
    </multiselect>

    <!-- En línea (junto al nombre del embarque) van solo las banderas y el +:
         el estado y el link a los listados harían ruido en el encabezado. -->
    <span v-if="inline && saving" class="small text-muted text-nowrap">
      <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
    </span>
    <div v-if="!inline" class="d-flex align-items-center gap-2 mt-1">
      <span v-if="saving" class="small text-muted">
        <i class="fas fa-circle-notch fa-spin me-1" aria-hidden="true"></i>
        {{ $t("financialSummary.stages.flagsSaving") }}
      </span>
      <span v-else-if="!selected.length" class="text-muted small fst-italic">
        {{ $t("financialSummary.stages.flagNone") }}
      </span>
      <router-link
        class="small"
        :to="manageListsTo"
        :title="$t('financialSummary.stages.flagsManageListsHint')"
      >
        {{ $t("financialSummary.stages.flagsManageLists") }}
      </router-link>
    </div>
  </div>
</template>

<script>
/**
 * Banderas (flags) de la operación: mismo catálogo compartido que la columna de
 * banderas del listado dinámico, y varias por operación.
 *
 * Se etiqueta acá mismo: el select elige una bandera del catálogo y se agrega como
 * tag; la × del tag la quita. La última opción del select crea una bandera nueva
 * (nombre y color) sin salir de la pantalla. Para renombrar una bandera o borrarla
 * se va a la lista de banderas. Presentacional: el padre guarda.
 */
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import FlagBadge from "@/components/gotrading/common/FlagBadge.vue";
import FlagOptionLabel from "@/components/gotrading/common/FlagOptionLabel.vue";
import { flagCatalogInTreeOrder } from "@/utils/financialMovementCategory";

export default {
  name: "OperationFlagsCard",
  components: { Multiselect, FlagBadge, FlagOptionLabel },
  props: {
    /** Banderas asignadas (nombres). */
    categories: { type: Array, default: () => [] },
    /** Catálogo de banderas del modelo, para las opciones y los colores. */
    catalog: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    /** Id de la lista de banderas del tenant (distinto en cada base). */
    listId: { type: String, default: "" },
    /** Un clic en el badge emite `pick-color` para abrir el popup del color. */
    colorPickable: { type: Boolean, default: false },
    /** En una sola línea (sin título ni pie): para el encabezado del embarque. */
    inline: { type: Boolean, default: false },
  },
  data() {
    return {
      /** En línea: el select está oculto hasta que se toca el +. */
      pickerOpen: false,
      /** Valor centinela de la opción "crear bandera" (ninguna bandera se llama así). */
      CREATE_OPTION: "__create_flag__",
    };
  },
  computed: {
    selected() {
      return (this.categories || []).filter(Boolean);
    },
    /** Banderas del catálogo en el orden del árbol de la lista, con su nivel. */
    catalogTree() {
      return flagCatalogInTreeOrder(this.catalog);
    },
    /** Banderas que todavía se pueden agregar: el catálogo menos lo ya asignado. */
    available() {
      const taken = this.selected.map((n) => String(n).toLowerCase());
      return this.catalogTree
        .map(({ entry }) => entry.name)
        .filter((name) => !taken.includes(name.toLowerCase()));
    },
    /**
     * Opciones del select. Las ya asignadas quedan deshabilitadas en vez de ocultas,
     * para que sus hijas no pierdan el padre bajo el que se ven.
     */
    pickerOptions() {
      const free = new Set(this.available.map((name) => name.toLowerCase()));
      return [
        ...this.catalogTree.map(({ entry, depth }) => ({
          key: `flag:${entry._id || entry.name}`,
          name: entry.name,
          color: entry.color,
          depth,
          $isDisabled: !free.has(entry.name.toLowerCase()),
        })),
        {
          key: this.CREATE_OPTION,
          name: this.$t("financialSummary.stages.flagsCreateOption"),
          create: true,
        },
      ];
    },
    /**
     * Link a la administración de banderas: la edición de la lista si sabemos su id,
     * y el listado de listas si el backend todavía no lo informó.
     */
    manageListsTo() {
      const id = String(this.listId || "").trim();
      return id ? `/lists/${id}/edit` : "/lists";
    },
  },
  methods: {
    /** El + deja ver el select y despliega sus opciones sin un segundo clic. */
    openPicker() {
      if (this.disabled) return;
      this.pickerOpen = true;
      this.$nextTick(() => {
        const picker = this.$refs.picker;
        if (picker) picker.activate();
      });
    },
    closePicker() {
      this.pickerOpen = false;
    },
    onSelect(option) {
      // El select no guarda valor (`:value="null"`): lo elegido ya se ve como tag.
      this.closePicker();
      if (!option) return;
      // Crear: el padre abre el popup y, si se confirma, la agrega como tag.
      if (option.create) {
        this.$emit("create");
        return;
      }
      const name = String(option.name || "").trim();
      if (!name) return;
      if (this.selected.some((n) => String(n).toLowerCase() === name.toLowerCase())) return;
      this.$emit("change", [...this.selected, name]);
    },
    /** Clic en el badge: el padre abre el popup y guarda el color en la lista. */
    pickColor(name) {
      if (!this.colorPickable || this.disabled) return;
      this.$emit("pick-color", name);
    },
    remove(name) {
      this.$emit(
        "change",
        this.selected.filter((n) => String(n).toLowerCase() !== String(name).toLowerCase())
      );
    },
  },
};
</script>

<style scoped>
.operation-flags-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.operation-flags-card__select {
  font-size: 0.8rem;
}

.operation-flags-card__select ::v-deep .multiselect__tags {
  min-height: 1.9rem;
  padding-top: 0.3rem;
  font-size: 0.8rem;
}

.operation-flags-card__select ::v-deep .multiselect__option {
  min-height: 0;
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  white-space: normal;
}

/* Las ya asignadas se ven, apagadas, para no romper la jerarquía. */
.operation-flags-card__select ::v-deep .multiselect__option--disabled {
  opacity: 0.45;
}

/* En línea: banderas y + en la misma fila del título, sin agrandarla de alto. */
.operation-flags-card--inline {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  line-height: 1;
}

.operation-flags-card--inline .operation-flags-card__tags {
  gap: 0.25rem;
}

/* Badge compacto: la fila del título no crece de alto por las banderas. */
.operation-flags-card--inline .operation-flags-card__tag {
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.35;
}

.operation-flags-card--inline .operation-flags-card__tag ::v-deep i {
  margin-top: 0;
}

/* En línea el cuadro es chico, pero el desplegable toma el ancho de las etiquetas. */
.operation-flags-card__select--inline {
  width: 12rem;
  min-height: 0;
  line-height: 1.2;
}

.operation-flags-card__select--inline ::v-deep .multiselect__tags {
  min-height: 1.45rem;
  padding: 0.15rem 1.8rem 0 0.4rem;
}

.operation-flags-card__select--inline ::v-deep .multiselect__select {
  height: 1.45rem;
}

.operation-flags-card__select--inline ::v-deep .multiselect__content-wrapper {
  width: max-content;
  min-width: 100%;
  max-width: 24rem;
}

/* El + ocupa lo mismo que un badge: la fila no cambia de alto. */
.operation-flags-card__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 1px dashed #adb5bd;
  border-radius: 0.25rem;
  background: none;
  color: #6c757d;
  font-size: 0.7rem;
  line-height: 1;
  cursor: pointer;
}

.operation-flags-card__add:hover:not(:disabled) {
  border-color: #0d6efd;
  color: #0d6efd;
}

.operation-flags-card__add:disabled {
  opacity: 0.45;
  cursor: default;
}

.operation-flags-card__tag--pickable {
  cursor: pointer;
}

.operation-flags-card__remove {
  background: none;
  border: 0;
  padding: 0;
  margin-left: 0.1rem;
  line-height: 1;
  font-size: 0.95rem;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
}

.operation-flags-card__remove:hover {
  opacity: 1;
}
</style>
